import {
	BUYER_ADMIN_ONLY,
	BUYER_STORE_ASSOCIATE,
	BUYER_USER,
	SUPER_ADMIN,
	SUPPLIER_ACCOUNTING_MANAGER,
	SUPPLIER_ADMIN_ONLY,
	SUPPLIER_INVENTORY_MANAGER,
	SUPPLIER_MANAGER,
	SUPPLIER_TECH,
	SUPPLIER_USER,
} from '../components/roles/roles';
import { nullSafeGet, nullSafeGetOrElse } from './DataAccessUtils';
import { ROLE_TYPES } from './DataConstants';
import moment from 'moment';
import platform from 'platform';

export const getBrowserAndDeviceInfo = () => {
	return {
		userAgent: navigator.userAgent,
		platform: navigator.platform,
		language: navigator.language,
		deviceMemory: (navigator as any).deviceMemory || 'unknown',
		cores: navigator.hardwareConcurrency || null,
		isMobile: /Mobi|Android/i.test(navigator.userAgent),
		browser: {
			name: platform.name,
			version: platform.version,
			layout: platform.layout,
			os: nullSafeGet('os', platform),
			description: platform.description,
		},
	};
};

export const checkAccountSetupNeeded = (user) => !(user.facility && user.facility.id);

export const isInternalTech = (currentUser) =>
	nullSafeGetOrElse('facility.status', currentUser, '') === 'private';

export const isThirdPartySupplier = (currentUser) =>
	nullSafeGet('userType', currentUser) === ROLE_TYPES.SUPPLIER && !isInternalTech(currentUser);

export const isBuyerOrInternalTech = (currentUser) =>
	nullSafeGet('userType', currentUser) === ROLE_TYPES.BUYER || isInternalTech(currentUser);

export const isAllowedToPerform = (permissionName, companySetting, roles) => {
	if (roles.some((_) => BUYER_ADMIN_ONLY.has(_))) {
		return true;
	} else {
		const permissionConfig = nullSafeGetOrElse(
			`config.${permissionName}`,
			companySetting,
			getBuyerAccessDefaultConfig(permissionName)
		);
		if (roles.includes(BUYER_USER)) {
			return permissionConfig.managersAllowed;
		} else if (roles.includes(BUYER_STORE_ASSOCIATE)) {
			return permissionConfig.storeAssociatesAllowed;
		}
	}

	return false;
};

export function validateDomain(email, companyNameArr) {
	if (companyNameArr.indexOf(email.split('@')[1]) === -1) {
		return false;
	}
	return true;
}

export const CONFIG_NAMES = {
	DISABLE_PURCHASE_ORDERS: 'disablePurchaseOrders',
	ALLOW_MONTHLY_CURRENCY_EXCHANGE_RATE: 'allowMonthlyCurrencyExchangeRate',
	ALLOW_PR_TO_FULFILL_WITHOUT_PO: 'allowPurchaseRequestToBeFulfilledWithoutPurchaseOrder',
};

export const PERMISSION_NAMES = {
	SHOW_LOCATION_DETAILS: 'showLocationDetails',
	MODIFY_LOCATIONS: 'modifyLocations',
	MODIFY_LOCATION_TYPES: 'modifyLocationTypes',
	MODIFY_AREAS: 'modifyAreas',
	MODIFY_SELF_LOCATION_ACCESS: 'modifySelfLocationAccess',
	SHOW_INTERNAL_TECH_NOTES: 'showInternalTechNotes',
	MODIFY_ASSETS: 'modifyAssets',
	DELETE_ASSETS: 'deleteAssets',
	MODIFY_ASSET_TYPES: 'modifyAssetTypes',
	SHOW_ASSET_PRICE: 'showAssetPrice',
	SHOW_ASSET_INTERNAL_ATTACHMENTS: 'showInternalAttachments',
	MODIFY_ASSET_INTERNAL_ATTACHMENTS: 'modifyInternalAttachments',
	CAN_MARK_WORK_ORDER_COMPLETED: 'canMarkWorkOrderCompleted',
	CAN_REVIEW_WORK_ORDER_COMPLETED: 'canReviewWorkOrderCompletion',
	ACCESS_ANALYTICS: 'accessAnalytics',
	MODIFY_PM: 'modifyPlannedMaintenance',
	MODIFY_SUPPLIERS: 'modifySuppliers',
	EDIT_SUPPLIER_NETWORK_ASSOCIATION: 'editSupplierNetworkAssociation',
	RANK_SUPPLIERS: 'rankSuppliers',
	EDIT_SUPPLIER_CONTACT: 'editSupplierContact',
	MODIFY_PARTS_AND_EQUIPMENTS: 'modifyPartsAndEquipments',
	MODIFY_VENDORS: 'modifyVendors',
	ACCESS_PR_AND_PO: 'accessPurchaseRequestAndPurchaseOrders',
	ACCESS_DEPRECIATION: 'accessDepreciationModule',
	CAN_BACKDATE_WORK_ORDER: 'canBackDateWorkOrder',
	CAN_UPLOAD_INVOICES_FOR_SUPPLIER: 'canUploadInvoicesForSupplier',
	CAN_UPLOAD_QUOTES_FOR_SUPPLIER: 'canUploadQuotesForSupplier',
	CAN_APPROVE_WORK_ORDERS: 'canApproveWorkOrders',
	CAN_APPROVE_QUOTES: 'canApproveQuotes',
	CAN_APPROVE_INVOICES: 'canApproveInvoices',
	CAN_MARK_INVOICES_PROCESSING_OR_PAID: 'canMarkInvoicesProcessingOrPaid',
	EDIT_WORK_ORDERS: 'editWorkOrder',
	CANCEL_WORK_ORDERS: 'cancelWorkOrder',
	REOPEN_WORK_ORDERS: 'reopenWorkOrder',
	VIEW_LINKED_WORK_ORDERS: 'viewLinkedWorkOrders',
	MODIFY_LINKING_WORK_ORDERS: 'modifyLinkingWorkOrders',
	MODIFY_SITE_SURVEYS: 'modifySiteSurveys',
	MODIFY_SITE_SURVEY_TEMPLATES: 'modifySiteSurveyTemplates',
	ADD_WALKTHROUGHS: 'addWalkthroughs',
	DELETE_WALKTHROUGHS: 'deleteWalkthroughs',
	DELETE_STOCK_LOCATIONS: 'deleteStockLocations',
	CAN_SHOW_ALL_SUPPLIERS_IN_WO_CREATION: 'supplierShowAllEnabledInWOCreation',
	CAN_VIEW_PART_EQUIPMENT_COST_IN_WO: 'viewPartEquipmentPriceInWorkOrder',
	MODIFY_WO_TASKS: 'modifyWorkOrderTasks',
	MODIFY_LOCATION_DOCUMENTS: 'modifyLocationDocuments',
	ACCESS_LOCATION_DOCUMENTS: 'accessLocationDocuments',
	CAN_VIEW_QUOTES: 'canViewQuotes',
	CAN_VIEW_INVOICES: 'canViewInvoices',
	CREATE_ASSET_CYCLE_EVENTS: 'canCreateAssetCycleCountEvents',
	EXECUTE_ASSET_CYCLE_RUNS: 'canExecuteAssetCycleCountRuns',
	VIEW_TROUBLESHOOTING: 'viewTroubleshootingGuide',
	ADD_TROUBLESHSHOOTING: 'addTroubleshootingGuide',
	MODIFY_TROUBLESHSHOOTING: 'modifyTroubleshootingGuide',
	DELETE_TROUBLESHSHOOTING: 'deleteTroubleshootingGuide',
	MODIFY_TROUBLESHSHOOTING_ASSOCIATIONS: 'modifyTroubleshootingAssociations',
	VIEW_BUDGET: 'viewBudget',
	ADD_BUDGET: 'addBudget',
	MODIFY_BUDGET: 'modifyBudget',
	DELETE_BUDGET: 'deleteBudget',
	VIEW_REPLACEMENT_INDEX: 'viewReplacementIndex',
	UPDATE_REPLACEMENT_INDEX: 'updateReplacementIndex',
	RUN_DEEP_ANALYSIS: 'runDeepAnalysis',
	REVISE_SERVICE_CALLS: 'allowServiceCallRevisions',
	ALLOW_BULK_OPERATIONS_FOR_PARTS: 'allowBulkOperationsForParts',
	ALLOW_BULK_OPERATIONS_FOR_EQUIPMENT: 'allowBulkOperationsForEquipment',
	ALLOW_BULK_OPERATIONS_WHILE_MANAGING_STOCK_LOCATION:
		'allowBulkOperationsWhileManagingStockLocation',
	MODIFY_WORK_ORDER_LABELS: 'modifyWorkOrderLabels',
	APPLY_WORK_ORDER_LABELS: 'applyWorkOrderLabels',
	MANAGE_STOCK_LOCATIONS: 'manageStockLocations',
	REMOTE_CHECK_IN_OR_CHECK_OUT_ALLOWED: 'remoteCheckInOrCheckOutAllowed',
	// Field Tech Management configurations — control which roles can assign each attribute on the
	// field tech detail page. Backed by the `fieldTechConfigurations` access-permission group.
	ASSIGN_ACCESS_TO_LOCATIONS: 'assignAccessToLocations',
	ASSIGN_ACCESS_TO_STOCK_LOCATIONS: 'assignAccessToStockLocations',
	ASSIGN_MANAGE_STOCK_LOCATIONS: 'assignManageStockLocations',
	ASSIGN_PROBLEM_TYPES: 'assignProblemTypes',
	ASSIGN_SUPERVISOR: 'assignSupervisor',
};

export const getSupplierAccessDefaultConfig = (permissionName) => {
	const ALL_RESTRICTED_DEFAULT = {
		managersAllowed: false,
		techsAllowed: false,
		usersAllowed: false,
		inventoryManagerAllowed: false,
		accountingManagerAllowed: false,
	};

	const ALL_ALLOWED_DEFAULT = {
		managersAllowed: true,
		techsAllowed: true,
		usersAllowed: true,
		inventoryManagerAllowed: true,
		accountingManagerAllowed: true,
	};

	const ALL_ALLOWED_EXCEPT_TECHS_DEFAULT = {
		...ALL_ALLOWED_DEFAULT,
		techsAllowed: false,
	};

	switch (permissionName) {
		case PERMISSION_NAMES.MANAGE_STOCK_LOCATIONS:
			return { ...ALL_ALLOWED_EXCEPT_TECHS_DEFAULT };
		case PERMISSION_NAMES.CAN_UPLOAD_INVOICES_FOR_SUPPLIER:
		case PERMISSION_NAMES.CAN_UPLOAD_QUOTES_FOR_SUPPLIER:
		case PERMISSION_NAMES.CAN_APPROVE_WORK_ORDERS:
		case PERMISSION_NAMES.CAN_APPROVE_INVOICES:
		case PERMISSION_NAMES.CAN_APPROVE_QUOTES:
		case PERMISSION_NAMES.CAN_MARK_INVOICES_PROCESSING_OR_PAID:
		case PERMISSION_NAMES.CAN_BACKDATE_WORK_ORDER:
		case PERMISSION_NAMES.DELETE_STOCK_LOCATIONS:
		case PERMISSION_NAMES.MODIFY_AREAS:
		case PERMISSION_NAMES.MODIFY_LOCATION_TYPES:
		case PERMISSION_NAMES.MODIFY_ASSET_TYPES:
		case PERMISSION_NAMES.CAN_VIEW_PART_EQUIPMENT_COST_IN_WO:
		case PERMISSION_NAMES.MODIFY_SUPPLIERS:
		case PERMISSION_NAMES.EDIT_SUPPLIER_NETWORK_ASSOCIATION:
		case PERMISSION_NAMES.RANK_SUPPLIERS:
		case PERMISSION_NAMES.EDIT_SUPPLIER_CONTACT:
		case PERMISSION_NAMES.MODIFY_ASSET_INTERNAL_ATTACHMENTS:
		case PERMISSION_NAMES.MODIFY_LOCATION_DOCUMENTS:
		case PERMISSION_NAMES.ACCESS_LOCATION_DOCUMENTS:
		case PERMISSION_NAMES.CREATE_ASSET_CYCLE_EVENTS:
		case PERMISSION_NAMES.EXECUTE_ASSET_CYCLE_RUNS:
		case PERMISSION_NAMES.VIEW_TROUBLESHOOTING:
		case PERMISSION_NAMES.ADD_TROUBLESHSHOOTING:
		case PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING:
		case PERMISSION_NAMES.DELETE_TROUBLESHSHOOTING:
		case PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING_ASSOCIATIONS:
		case PERMISSION_NAMES.VIEW_BUDGET:
		case PERMISSION_NAMES.ADD_BUDGET:
		case PERMISSION_NAMES.MODIFY_BUDGET:
		case PERMISSION_NAMES.DELETE_BUDGET:
		case PERMISSION_NAMES.DELETE_ASSETS:
		case PERMISSION_NAMES.UPDATE_REPLACEMENT_INDEX:
		case PERMISSION_NAMES.RUN_DEEP_ANALYSIS:
		case PERMISSION_NAMES.REVISE_SERVICE_CALLS:
		case PERMISSION_NAMES.ALLOW_BULK_OPERATIONS_FOR_PARTS:
		case PERMISSION_NAMES.ALLOW_BULK_OPERATIONS_FOR_EQUIPMENT:
		case PERMISSION_NAMES.ALLOW_BULK_OPERATIONS_WHILE_MANAGING_STOCK_LOCATION:
		// Field Tech Management configs are all-restricted by default; roles must be granted explicitly.
		case PERMISSION_NAMES.ASSIGN_ACCESS_TO_LOCATIONS:
		case PERMISSION_NAMES.ASSIGN_ACCESS_TO_STOCK_LOCATIONS:
		case PERMISSION_NAMES.ASSIGN_MANAGE_STOCK_LOCATIONS:
		case PERMISSION_NAMES.ASSIGN_PROBLEM_TYPES:
		case PERMISSION_NAMES.ASSIGN_SUPERVISOR:
		// Work order labels are admin-only by default; non-admin roles must be granted access explicitly.
		case PERMISSION_NAMES.MODIFY_WORK_ORDER_LABELS:
		case PERMISSION_NAMES.APPLY_WORK_ORDER_LABELS:
			return { ...ALL_RESTRICTED_DEFAULT };
		case PERMISSION_NAMES.MODIFY_SITE_SURVEYS:
		case PERMISSION_NAMES.EDIT_WORK_ORDERS:
		case PERMISSION_NAMES.CANCEL_WORK_ORDERS:
		case PERMISSION_NAMES.MODIFY_SITE_SURVEY_TEMPLATES:
		case PERMISSION_NAMES.ADD_WALKTHROUGHS:
		case PERMISSION_NAMES.DELETE_WALKTHROUGHS:
		case PERMISSION_NAMES.REOPEN_WORK_ORDERS:
		case PERMISSION_NAMES.MODIFY_WO_TASKS:
		case PERMISSION_NAMES.SHOW_ASSET_INTERNAL_ATTACHMENTS:
		case PERMISSION_NAMES.CAN_VIEW_QUOTES:
		case PERMISSION_NAMES.CAN_VIEW_INVOICES:
		case PERMISSION_NAMES.VIEW_LINKED_WORK_ORDERS:
		case PERMISSION_NAMES.MODIFY_LINKING_WORK_ORDERS:
		case PERMISSION_NAMES.REMOTE_CHECK_IN_OR_CHECK_OUT_ALLOWED:
		default:
			return { ...ALL_ALLOWED_DEFAULT };
	}
};

export const getBuyerAccessDefaultConfig = (permissionKey?) => {
	const ALL_RESTRICTED_DEFAULT = {
		managersAllowed: false,
		storeAssociatesAllowed: false,
	};

	const ALL_ALLOWED_DEFAULT = {
		managersAllowed: true,
		storeAssociatesAllowed: true,
	};

	const ONLY_MANAGERS_ALLOWED_DEFAULT = {
		managersAllowed: true,
		storeAssociatesAllowed: false,
	};

	switch (permissionKey) {
		case PERMISSION_NAMES.CAN_MARK_WORK_ORDER_COMPLETED:
		case PERMISSION_NAMES.CAN_APPROVE_WORK_ORDERS:
		case PERMISSION_NAMES.CAN_APPROVE_QUOTES:
		case PERMISSION_NAMES.CAN_APPROVE_INVOICES:
		case PERMISSION_NAMES.EDIT_WORK_ORDERS:
		case PERMISSION_NAMES.CAN_SHOW_ALL_SUPPLIERS_IN_WO_CREATION:
		case PERMISSION_NAMES.EDIT_SUPPLIER_NETWORK_ASSOCIATION:
		case PERMISSION_NAMES.MODIFY_PM:
		case PERMISSION_NAMES.MODIFY_LOCATION_DOCUMENTS:
		case PERMISSION_NAMES.ACCESS_LOCATION_DOCUMENTS:
			return ONLY_MANAGERS_ALLOWED_DEFAULT;
		case PERMISSION_NAMES.CAN_UPLOAD_INVOICES_FOR_SUPPLIER:
		case PERMISSION_NAMES.CAN_UPLOAD_QUOTES_FOR_SUPPLIER:
		case PERMISSION_NAMES.MODIFY_AREAS:
		case PERMISSION_NAMES.MODIFY_LOCATION_TYPES:
		case PERMISSION_NAMES.MODIFY_ASSET_TYPES:
		case PERMISSION_NAMES.CAN_MARK_INVOICES_PROCESSING_OR_PAID:
		case PERMISSION_NAMES.MODIFY_SUPPLIERS:
		case PERMISSION_NAMES.RANK_SUPPLIERS:
		case PERMISSION_NAMES.EDIT_SUPPLIER_CONTACT:
		case PERMISSION_NAMES.MODIFY_ASSET_INTERNAL_ATTACHMENTS:
		case PERMISSION_NAMES.CREATE_ASSET_CYCLE_EVENTS:
		case PERMISSION_NAMES.EXECUTE_ASSET_CYCLE_RUNS:
		case PERMISSION_NAMES.VIEW_TROUBLESHOOTING:
		case PERMISSION_NAMES.ADD_TROUBLESHSHOOTING:
		case PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING:
		case PERMISSION_NAMES.DELETE_TROUBLESHSHOOTING:
		case PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING_ASSOCIATIONS:
		case PERMISSION_NAMES.VIEW_BUDGET:
		case PERMISSION_NAMES.ADD_BUDGET:
		case PERMISSION_NAMES.MODIFY_BUDGET:
		case PERMISSION_NAMES.DELETE_BUDGET:
		case PERMISSION_NAMES.DELETE_ASSETS:
		case PERMISSION_NAMES.UPDATE_REPLACEMENT_INDEX:
		case PERMISSION_NAMES.RUN_DEEP_ANALYSIS:
		// Work order labels are admin-only by default; non-admin roles must be granted access explicitly.
		case PERMISSION_NAMES.MODIFY_WORK_ORDER_LABELS:
		case PERMISSION_NAMES.APPLY_WORK_ORDER_LABELS:
			return { ...ALL_RESTRICTED_DEFAULT };
		case PERMISSION_NAMES.CAN_REVIEW_WORK_ORDER_COMPLETED:
		case PERMISSION_NAMES.CANCEL_WORK_ORDERS:
		case PERMISSION_NAMES.REOPEN_WORK_ORDERS:
		case PERMISSION_NAMES.MODIFY_SITE_SURVEYS:
		case PERMISSION_NAMES.MODIFY_SITE_SURVEY_TEMPLATES:
		case PERMISSION_NAMES.ADD_WALKTHROUGHS:
		case PERMISSION_NAMES.DELETE_WALKTHROUGHS:
		case PERMISSION_NAMES.MODIFY_SELF_LOCATION_ACCESS:
		case PERMISSION_NAMES.SHOW_LOCATION_DETAILS:
		case PERMISSION_NAMES.MODIFY_WO_TASKS:
		case PERMISSION_NAMES.SHOW_ASSET_INTERNAL_ATTACHMENTS:
		case PERMISSION_NAMES.CAN_VIEW_QUOTES:
		case PERMISSION_NAMES.CAN_VIEW_INVOICES:
		case PERMISSION_NAMES.VIEW_LINKED_WORK_ORDERS:
		case PERMISSION_NAMES.MODIFY_LINKING_WORK_ORDERS:
		default:
			return { ...ALL_ALLOWED_DEFAULT };
	}
};

export const getSupplierConfigForPermission = (permissionName, companySetting) => {
	const keys = {
		[PERMISSION_NAMES.MODIFY_LOCATIONS]: ['locations'],
		[PERMISSION_NAMES.MODIFY_LOCATION_TYPES]: ['locations'],
		[PERMISSION_NAMES.MODIFY_AREAS]: ['locations'],
		[PERMISSION_NAMES.SHOW_INTERNAL_TECH_NOTES]: ['locations'],
		[PERMISSION_NAMES.DELETE_STOCK_LOCATIONS]: ['locations'],
		[PERMISSION_NAMES.MODIFY_ASSETS]: ['assets'],
		[PERMISSION_NAMES.DELETE_ASSETS]: ['assets'],
		[PERMISSION_NAMES.MODIFY_ASSET_TYPES]: ['assets'],
		[PERMISSION_NAMES.SHOW_ASSET_PRICE]: ['assets'],
		[PERMISSION_NAMES.SHOW_ASSET_INTERNAL_ATTACHMENTS]: ['assets'],
		[PERMISSION_NAMES.MODIFY_ASSET_INTERNAL_ATTACHMENTS]: ['assets'],
		[PERMISSION_NAMES.CAN_MARK_WORK_ORDER_COMPLETED]: ['approvals'],
		[PERMISSION_NAMES.CAN_REVIEW_WORK_ORDER_COMPLETED]: ['approvals'],
		[PERMISSION_NAMES.ACCESS_ANALYTICS]: ['others'],
		[PERMISSION_NAMES.MODIFY_PM]: ['others'],
		[PERMISSION_NAMES.MODIFY_SUPPLIERS]: ['others'],
		[PERMISSION_NAMES.EDIT_SUPPLIER_NETWORK_ASSOCIATION]: ['others'],
		[PERMISSION_NAMES.RANK_SUPPLIERS]: ['others'],
		[PERMISSION_NAMES.EDIT_SUPPLIER_CONTACT]: ['others'],
		[PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS]: ['others'],
		[PERMISSION_NAMES.MODIFY_VENDORS]: ['others'],
		[PERMISSION_NAMES.ACCESS_PR_AND_PO]: ['others'],
		[PERMISSION_NAMES.ACCESS_DEPRECIATION]: ['others'],
		[PERMISSION_NAMES.CAN_BACKDATE_WORK_ORDER]: ['others'],
		[PERMISSION_NAMES.CAN_UPLOAD_INVOICES_FOR_SUPPLIER]: ['others'],
		[PERMISSION_NAMES.CAN_UPLOAD_QUOTES_FOR_SUPPLIER]: ['others'],
		[PERMISSION_NAMES.REOPEN_WORK_ORDERS]: ['others'],
		[PERMISSION_NAMES.VIEW_LINKED_WORK_ORDERS]: ['others'],
		[PERMISSION_NAMES.MODIFY_LINKING_WORK_ORDERS]: ['others'],
		[PERMISSION_NAMES.CAN_VIEW_PART_EQUIPMENT_COST_IN_WO]: ['others'],
		[PERMISSION_NAMES.MODIFY_WO_TASKS]: ['others'],
		[PERMISSION_NAMES.EDIT_WORK_ORDERS]: ['others'],
		[PERMISSION_NAMES.CANCEL_WORK_ORDERS]: ['others'],
		[PERMISSION_NAMES.CAN_VIEW_QUOTES]: ['others'],
		[PERMISSION_NAMES.CAN_VIEW_INVOICES]: ['others'],
		[PERMISSION_NAMES.CAN_APPROVE_WORK_ORDERS]: ['approvals'],
		[PERMISSION_NAMES.CAN_APPROVE_QUOTES]: ['approvals'],
		[PERMISSION_NAMES.CAN_APPROVE_INVOICES]: ['approvals'],
		[PERMISSION_NAMES.CAN_MARK_INVOICES_PROCESSING_OR_PAID]: ['approvals'],
		[PERMISSION_NAMES.MODIFY_SITE_SURVEYS]: ['siteSurveys'],
		[PERMISSION_NAMES.MODIFY_SITE_SURVEY_TEMPLATES]: ['siteSurveys'],
		[PERMISSION_NAMES.ADD_WALKTHROUGHS]: ['siteSurveys'],
		[PERMISSION_NAMES.DELETE_WALKTHROUGHS]: ['siteSurveys'],
		[PERMISSION_NAMES.MODIFY_LOCATION_DOCUMENTS]: ['locations'],
		[PERMISSION_NAMES.ACCESS_LOCATION_DOCUMENTS]: ['locations'],
		[PERMISSION_NAMES.VIEW_TROUBLESHOOTING]: ['workflows'],
		[PERMISSION_NAMES.ADD_TROUBLESHSHOOTING]: ['workflows'],
		[PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING]: ['workflows'],
		[PERMISSION_NAMES.DELETE_TROUBLESHSHOOTING]: ['workflows'],
		[PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING_ASSOCIATIONS]: ['workflows'],
		[PERMISSION_NAMES.VIEW_BUDGET]: ['workflows'],
		[PERMISSION_NAMES.ADD_BUDGET]: ['workflows'],
		[PERMISSION_NAMES.MODIFY_BUDGET]: ['workflows'],
		[PERMISSION_NAMES.DELETE_BUDGET]: ['workflows'],
		[PERMISSION_NAMES.VIEW_REPLACEMENT_INDEX]: ['replacementIndex'],
		[PERMISSION_NAMES.UPDATE_REPLACEMENT_INDEX]: ['replacementIndex'],
		[PERMISSION_NAMES.RUN_DEEP_ANALYSIS]: ['replacementIndex'],
		[PERMISSION_NAMES.REVISE_SERVICE_CALLS]: ['others'],
		[PERMISSION_NAMES.ALLOW_BULK_OPERATIONS_FOR_PARTS]: ['others'],
		[PERMISSION_NAMES.ALLOW_BULK_OPERATIONS_FOR_EQUIPMENT]: ['others'],
		[PERMISSION_NAMES.ALLOW_BULK_OPERATIONS_WHILE_MANAGING_STOCK_LOCATION]: ['stockLocations'],
		[PERMISSION_NAMES.MODIFY_WORK_ORDER_LABELS]: ['labels'],
		[PERMISSION_NAMES.APPLY_WORK_ORDER_LABELS]: ['labels'],
		[PERMISSION_NAMES.MANAGE_STOCK_LOCATIONS]: ['stockLocations'],
		[PERMISSION_NAMES.REMOTE_CHECK_IN_OR_CHECK_OUT_ALLOWED]: ['workOrders'],
		[PERMISSION_NAMES.ASSIGN_ACCESS_TO_LOCATIONS]: ['fieldTechConfigurations'],
		[PERMISSION_NAMES.ASSIGN_ACCESS_TO_STOCK_LOCATIONS]: ['fieldTechConfigurations'],
		[PERMISSION_NAMES.ASSIGN_MANAGE_STOCK_LOCATIONS]: ['fieldTechConfigurations'],
		[PERMISSION_NAMES.ASSIGN_PROBLEM_TYPES]: ['fieldTechConfigurations'],
		[PERMISSION_NAMES.ASSIGN_SUPERVISOR]: ['fieldTechConfigurations'],
	};

	const keyName = [...(keys[permissionName] || []), ...[permissionName]].join('.');

	return nullSafeGetOrElse(
		`config.accessPermissions.${keyName}`,
		companySetting,
		getSupplierAccessDefaultConfig(permissionName)
	);
};

export const isSupplierAllowedToAccess = (permissionName, companySetting, roles = []) => {
	if (roles.some((_) => SUPPLIER_ADMIN_ONLY.has(_))) {
		return true;
	} else {
		const permissionConfig = getSupplierConfigForPermission(permissionName, companySetting);
		const roleName = {
			[SUPPLIER_TECH]: 'techsAllowed',
			[SUPPLIER_USER]: 'usersAllowed',
			[SUPPLIER_MANAGER]: 'managersAllowed',
			[SUPPLIER_INVENTORY_MANAGER]: 'inventoryManagerAllowed',
			[SUPPLIER_ACCOUNTING_MANAGER]: 'accountingManagerAllowed',
		};

		return roles.map((_) => nullSafeGet(`${roleName[_]}`, permissionConfig)).some((_) => !!_);
	}
};

export const getBuyerConfigForPermission = (permissionName, companySetting) => {
	const keys = {
		[PERMISSION_NAMES.CAN_MARK_WORK_ORDER_COMPLETED]: [],
		[PERMISSION_NAMES.CAN_REVIEW_WORK_ORDER_COMPLETED]: [],
		[PERMISSION_NAMES.SHOW_LOCATION_DETAILS]: ['accessPermissions', 'locations'],
		[PERMISSION_NAMES.MODIFY_LOCATIONS]: ['accessPermissions', 'locations'],
		[PERMISSION_NAMES.MODIFY_LOCATION_TYPES]: ['accessPermissions', 'locations'],
		[PERMISSION_NAMES.MODIFY_AREAS]: ['accessPermissions', 'locations'],
		[PERMISSION_NAMES.MODIFY_SELF_LOCATION_ACCESS]: ['accessPermissions', 'locations'],
		[PERMISSION_NAMES.SHOW_INTERNAL_TECH_NOTES]: ['accessPermissions', 'locations'],
		[PERMISSION_NAMES.MODIFY_ASSETS]: ['accessPermissions', 'assets'],
		[PERMISSION_NAMES.DELETE_ASSETS]: ['accessPermissions', 'assets'],
		[PERMISSION_NAMES.MODIFY_ASSET_TYPES]: ['accessPermissions', 'assets'],
		[PERMISSION_NAMES.SHOW_ASSET_PRICE]: ['accessPermissions', 'assets'],
		[PERMISSION_NAMES.SHOW_ASSET_INTERNAL_ATTACHMENTS]: ['accessPermissions', 'assets'],
		[PERMISSION_NAMES.MODIFY_ASSET_INTERNAL_ATTACHMENTS]: ['accessPermissions', 'assets'],
		[PERMISSION_NAMES.ACCESS_ANALYTICS]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.CAN_UPLOAD_INVOICES_FOR_SUPPLIER]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.CAN_UPLOAD_QUOTES_FOR_SUPPLIER]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.MODIFY_SUPPLIERS]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.EDIT_SUPPLIER_NETWORK_ASSOCIATION]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.RANK_SUPPLIERS]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.EDIT_SUPPLIER_CONTACT]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.EDIT_WORK_ORDERS]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.CANCEL_WORK_ORDERS]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.REOPEN_WORK_ORDERS]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.VIEW_LINKED_WORK_ORDERS]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.MODIFY_LINKING_WORK_ORDERS]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.CAN_SHOW_ALL_SUPPLIERS_IN_WO_CREATION]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.MODIFY_WO_TASKS]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.MODIFY_PM]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.CAN_VIEW_QUOTES]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.CAN_VIEW_INVOICES]: ['accessPermissions', 'others'],
		[PERMISSION_NAMES.MODIFY_SITE_SURVEYS]: ['accessPermissions', 'siteSurveys'],
		[PERMISSION_NAMES.MODIFY_SITE_SURVEY_TEMPLATES]: ['accessPermissions', 'siteSurveys'],
		[PERMISSION_NAMES.ADD_WALKTHROUGHS]: ['accessPermissions', 'siteSurveys'],
		[PERMISSION_NAMES.DELETE_WALKTHROUGHS]: ['accessPermissions', 'siteSurveys'],
		[PERMISSION_NAMES.MODIFY_LOCATION_DOCUMENTS]: ['accessPermissions', 'locations'],
		[PERMISSION_NAMES.ACCESS_LOCATION_DOCUMENTS]: ['accessPermissions', 'locations'],
		[PERMISSION_NAMES.VIEW_TROUBLESHOOTING]: ['accessPermissions', 'workflows'],
		[PERMISSION_NAMES.ADD_TROUBLESHSHOOTING]: ['accessPermissions', 'workflows'],
		[PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING]: ['accessPermissions', 'workflows'],
		[PERMISSION_NAMES.DELETE_TROUBLESHSHOOTING]: ['accessPermissions', 'workflows'],
		[PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING_ASSOCIATIONS]: ['accessPermissions', 'workflows'],
		[PERMISSION_NAMES.VIEW_BUDGET]: ['accessPermissions', 'workflows'],
		[PERMISSION_NAMES.ADD_BUDGET]: ['accessPermissions', 'workflows'],
		[PERMISSION_NAMES.MODIFY_BUDGET]: ['accessPermissions', 'workflows'],
		[PERMISSION_NAMES.DELETE_BUDGET]: ['accessPermissions', 'workflows'],
		[PERMISSION_NAMES.VIEW_REPLACEMENT_INDEX]: ['accessPermissions', 'replacementIndex'],
		[PERMISSION_NAMES.UPDATE_REPLACEMENT_INDEX]: ['accessPermissions', 'replacementIndex'],
		[PERMISSION_NAMES.RUN_DEEP_ANALYSIS]: ['accessPermissions', 'replacementIndex'],
		[PERMISSION_NAMES.MODIFY_WORK_ORDER_LABELS]: ['accessPermissions', 'labels'],
		[PERMISSION_NAMES.APPLY_WORK_ORDER_LABELS]: ['accessPermissions', 'labels'],
		[PERMISSION_NAMES.REMOTE_CHECK_IN_OR_CHECK_OUT_ALLOWED]: ['accessPermissions', 'workOrders'],
	};

	const keyName = [...(keys[permissionName] || []), ...[permissionName]].join('.');
	return nullSafeGetOrElse(
		`config.${keyName}`,
		companySetting,
		getBuyerAccessDefaultConfig(permissionName)
	);
};

export const isBuyerAllowedToAccess = (permissionName, companySetting, roles = []) => {
	if (roles.some((_) => BUYER_ADMIN_ONLY.has(_))) {
		return true;
	} else {
		const permissionConfig = getBuyerConfigForPermission(permissionName, companySetting);

		const roleName = {
			[BUYER_USER]: 'managersAllowed',
			[BUYER_STORE_ASSOCIATE]: 'storeAssociatesAllowed',
		};

		return roles.map((_) => nullSafeGet(`${roleName[_]}`, permissionConfig)).some((_) => !!_);
	}
};

export const isUserAllowedToAccess =
	(userType) =>
	(permissionName, companySetting, roles = []) =>
		userType === ROLE_TYPES.SUPPLIER
			? isSupplierAllowedToAccess(permissionName, companySetting, roles)
			: isBuyerAllowedToAccess(permissionName, companySetting, roles);

export const getBuyerCompanySettingValue =
	(userType) =>
	(name, companyConfig, currentUser, defaultValue = false) =>
		userType === ROLE_TYPES.SUPPLIER
			? nullSafeGetOrElse(
					`facility.privateBuyerCompanySettings.config.${name}`,
					currentUser,
					defaultValue
			  )
			: nullSafeGetOrElse(`config.${name}`, companyConfig, defaultValue);

export const isUserManagedBySupplier = (currentUser) =>
	!nullSafeGetOrElse('roles', currentUser, []).includes(SUPER_ADMIN) &&
	nullSafeGetOrElse(`facility.buyerCompany.supplierManagedCompany`, currentUser, false);

export const isBetweenMoments = (startDate, endDate, compareDate = undefined) =>
	moment(compareDate).diff(moment(startDate)) > 0 && moment(endDate).diff(moment(compareDate)) > 0;

export const getRolePermissionKeyMap = (userType: string): Record<string, string> => {
	if (userType === ROLE_TYPES.BUYER) {
		return {
			[BUYER_USER]: 'managersAllowed',
			[BUYER_STORE_ASSOCIATE]: 'storeAssociatesAllowed',
		};
	} else if (userType === ROLE_TYPES.SUPPLIER) {
		return {
			[SUPPLIER_TECH]: 'techsAllowed',
			[SUPPLIER_USER]: 'usersAllowed',
			[SUPPLIER_MANAGER]: 'managersAllowed',
			[SUPPLIER_INVENTORY_MANAGER]: 'inventoryManagerAllowed',
			[SUPPLIER_ACCOUNTING_MANAGER]: 'accountingManagerAllowed',
		};
	}
	return {};
};
